import {
  AdviceProductDetailsBackendValues,
  AdviceProductDetailsFormValues,
  AdvisorValues,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { Advice, AdviceState } from "Store/advice/interfaces";
import {
  adviceDetailsFormValuesSelector,
  adviceSelector,
} from "Store/advice/stateSelectors";
import {
  AdditionalAdviceScreenshotData,
  AdditionalAkaAdviceScreenshotData,
  AdvicePDFResponse,
  AdvicePDFState,
  AdviceType,
  OperationType,
} from "Store/advicePDF/interfaces";
import {
  akaAdvicePriceFormValuesSelector,
  akaAdviceResultSelector,
} from "Store/akaAdvice/akaAdviceSelectors";
import { createRequest, getError } from "Utils/requestUtils";
import {
  CallEffect,
  SelectEffect,
  call,
  put,
  select,
  takeLatest,
} from "redux-saga/effects";
import { AdviceChartURIs, getAdviceCharts } from "./adviceCharts";
import {
  advicePDFStart,
  advicePDFSucceeded,
  akaAdvicePDFStart,
  operationFailed,
  updateAdvicePDFRequestState,
} from "./slice";

import { mapDetailsFormDataToAdviceData } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/utils";
import { CustomerInfo } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceScreenshot/interfaces";
import { generateScreenshot } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceScreenshot/utils";
import { AkaAdvicePriceFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/interfaces";
import { generateIndividualAdviceScreenshot } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceScreenshot/individualAdviceScreenshotUtils";
import { generateCollectiveAdviceScreenshot } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceScreenshot/utils";
import { getAdvisorValues } from "Store/advice/sagas";
import { isAdvisorSelector } from "Store/advisor/advisorSelectors";
import { AdviceVersion } from "Store/akaAdvice/akaAdviceHooks";
import { CollectiveAdvice } from "Store/akaAdvice/interfaces/CollectiveAdvice";
import { ReduxState } from "Store/rootReducer";
import { UserIdentificationData } from "Store/userIdentification";
import { getCustomerNinInSaga } from "Store/userIdentification/userIdentificationHooks";
import { userIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { recordAdvicePdfIfRecording } from "Utils/fixtureRecordingUtils";
import isEqual from "lodash.isequal";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* advicePDF({ payload }: ReturnType<typeof advicePDFStart>) {
  try {
    const advice: AdviceState = yield select(adviceSelector);
    const isAdvisor: boolean = yield select(isAdvisorSelector);
    const adviceScreenshotData = generateScreenshot(
      advice.adviceResult.current as Advice,
      payload.ref,
    );

    if (adviceScreenshotData) {
      const customerInfo: CustomerInfo = yield call(getCustomerInfo);
      const advicePayload: AdviceProductDetailsBackendValues = yield call(
        getAdvicePayload,
        isAdvisor,
      );

      const requestBody: AdditionalAdviceScreenshotData = {
        ...adviceScreenshotData,
        customerInfo,
        advicePayload,
        initialAdvice: payload.initialAdvice ?? false,
      };

      yield call(handleAdvicePDFRequest, requestBody, AdviceVersion.OLD);
    } else {
      throw new Error("Unable to generate advice screenshot data");
    }
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.SAVE_ADVICE_PDF_ERROR,
        response: getError(e),
      }),
    );
    if (payload.throwNewError) {
      throw new Error(
        `Operation failed for type ${OperationType.SAVE_ADVICE_PDF_ERROR}`,
      );
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* akaAdvicePDF({
  payload,
}: ReturnType<typeof akaAdvicePDFStart>) {
  try {
    const advice: CollectiveAdvice = yield select(akaAdviceResultSelector);
    const akaAdvicePriceFormValues: AkaAdvicePriceFormValues | undefined =
      yield select(akaAdvicePriceFormValuesSelector);
    yield call(delay, 200);
    const adviceCharts: AdviceChartURIs = yield call(
      getAdviceCharts,
      payload.ref,
    );

    const isAdvisor: boolean = yield select(isAdvisorSelector);
    const adviceScreenshotData =
      payload.adviceType === AdviceType.INDIVIDUAL
        ? generateIndividualAdviceScreenshot(
            advice,
            akaAdvicePriceFormValues,
            payload.ref,
          )
        : generateCollectiveAdviceScreenshot(
            advice,
            adviceCharts,
            akaAdvicePriceFormValues,
            payload.ref,
          );

    if (adviceScreenshotData) {
      const customerInfo: CustomerInfo = yield call(getCustomerInfo);
      const adviceDetailsFormValues: AdviceProductDetailsFormValues =
        yield select(adviceDetailsFormValuesSelector);
      const advicePayload = mapDetailsFormDataToAdviceData(
        adviceDetailsFormValues,
        isAdvisor,
      );

      const requestBody: AdditionalAkaAdviceScreenshotData = {
        ...adviceScreenshotData,
        customerInfo,
        advicePayload,
        initialAdvice: payload.initialAdvice ?? false,
      };

      if (payload.adviceType === AdviceType.INDIVIDUAL) {
        yield call(handleAdvicePDFRequest, requestBody, AdviceVersion.NEW);
      } else {
        yield call(handleAkaAdvicePDFRequest, requestBody);
      }
    } else {
      throw new Error("Unable to generate advice screenshot data");
    }
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.SAVE_ADVICE_PDF_ERROR,
        response: getError(e),
      }),
    );
    if (payload.throwNewError) {
      throw new Error(
        `Operation failed for type ${OperationType.SAVE_ADVICE_PDF_ERROR}`,
      );
    }
  }
}

export function* getCustomerInfo(): Generator<
  SelectEffect | CallEffect<unknown>,
  CustomerInfo,
  UserIdentificationData & string
> {
  const userIdentificationData: UserIdentificationData = yield select(
    userIdentificationSelector,
  );
  const newCustomerResponse = userIdentificationData?.newCustomerResponse;
  const customerName = newCustomerResponse?.providedPersonInfo?.name;
  const customerNin = (yield call(getCustomerNinInSaga)) as string;

  return {
    nin: customerNin?.toString(),
    firstName: customerName?.firstName,
    lastName: customerName?.lastName,
    dateOfBirth: newCustomerResponse?.dateOfBirth,
    address: newCustomerResponse?.providedContactInfo?.address,
  };
}

function* getAdvicePayload(
  isAdvisor: boolean,
): Generator<
  CallEffect | SelectEffect,
  AdviceProductDetailsBackendValues,
  AdvisorValues & AdviceProductDetailsFormValues
> {
  const advisorValues: AdvisorValues = yield call(getAdvisorValues);
  const adviceDetailsFormValues: AdviceProductDetailsFormValues = yield select(
    adviceDetailsFormValuesSelector,
  );
  return mapDetailsFormDataToAdviceData(
    adviceDetailsFormValues,
    isAdvisor,
    advisorValues,
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* handleAdvicePDFRequest(
  requestBody:
    | AdditionalAdviceScreenshotData
    | AdditionalAkaAdviceScreenshotData,
  adviceVersion: AdviceVersion,
) {
  const advicePDFState: AdvicePDFState = yield select(
    (state: ReduxState): AdvicePDFState => state.advicePDF,
  );
  const url =
    adviceVersion === AdviceVersion.OLD
      ? "/open/insurance/communication/advice"
      : "/open/insurance/communication/individualadvice";

  if (
    advicePDFState.request &&
    isEqual(advicePDFState.request, requestBody) &&
    advicePDFState.response.current
  ) {
    yield put(advicePDFSucceeded(advicePDFState.response.current));
  } else {
    yield put(updateAdvicePDFRequestState(requestBody));
    const { data } = yield call(createRequest, {
      method: "post",
      requestBody,
      url,
    });
    recordAdvicePdfIfRecording(data);
    yield put(advicePDFSucceeded(data as AdvicePDFResponse));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* handleAkaAdvicePDFRequest(
  requestBody: AdditionalAkaAdviceScreenshotData,
) {
  const advicePDFState: AdvicePDFState = yield select(
    (state: ReduxState): AdvicePDFState => state.advicePDF,
  );

  if (
    advicePDFState.request &&
    isEqual(advicePDFState.request, requestBody) &&
    advicePDFState.response.current
  ) {
    yield put(advicePDFSucceeded(advicePDFState.response.current));
  } else {
    yield put(updateAdvicePDFRequestState(requestBody));
    const { data } = yield call(createRequest, {
      method: "post",
      requestBody,
      url: "/insurance/communication/collectiveadvice",
      open: false,
    });
    recordAdvicePdfIfRecording(data);
    yield put(advicePDFSucceeded(data as AdvicePDFResponse));
  }
}

export function* advicePDFSaga(): Generator {
  yield takeLatest(advicePDFStart, advicePDF);
  yield takeLatest(akaAdvicePDFStart, akaAdvicePDF);
}

const delay = (time: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, time));
